import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { TRANSLATIONS } from '../constants';
import { CheckCircle, Plus, Minus, Trash2 } from 'lucide-react';
import { updateMetaTags } from '../utils/seo';
import { SEO_CONFIG, getFullUrl } from '../config/seo.config';

export const Checkout = () => {
  const { cart, placeOrder, updateQuantity, removeFromCart, language } = useStore();
  const navigate = useNavigate();
  const t = TRANSLATIONS;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zip: '',
    taxNumber: '',
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    updateMetaTags({
      title: `${t.checkout[language]} - ${SEO_CONFIG.storeName}`,
      description: 'Complete your purchase securely',
      canonical: getFullUrl('/checkout'),
    });
  }, [language, t]);

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const vat = subtotal * 0.23;
  const total = subtotal + vat;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    const result = await placeOrder(formData);

    setIsSubmitting(false);
    if (result.success) {
      setIsSuccess(true);
    } else {
      setSubmitError(result.error || 'Failed to place order. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (isSuccess) {
    return (
      <main className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-6 animate-bounce">
          <CheckCircle className="w-10 h-10 text-emerald-600" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">{t.orderSuccess[language]}</h2>
        <p className="text-gray-500 mb-8">{language === 'en' ? 'Thank you for your order!' : 'Dziękujemy za zamówienie!'}</p>
        <div className="flex flex-col sm:flex-row gap-4 w-full max-w-sm">
          <button
            onClick={() => navigate('/orders')}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg shadow-emerald-200"
          >
            {t.viewOrder[language]}
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex-1 bg-white border-2 border-gray-200 hover:border-emerald-500 text-gray-700 hover:text-emerald-600 font-bold py-3 px-6 rounded-xl transition-all"
          >
            {t.continueShopping[language]}
          </button>
        </div>
      </main>
    );
  }

  if (cart.length === 0) {
      navigate('/cart');
      return null;
  }

  return (
    <main className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
      <div>
        <h2 className="text-2xl font-bold mb-6">{t.checkout[language]}</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.name[language]} <span className="text-red-500">*</span></label>
            <input
              required
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.email[language]} <span className="text-red-500">*</span></label>
            <input
              required
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.phone[language]} <span className="text-red-500">*</span></label>
            <input
              required
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.taxNumber[language]}</label>
            <input
              name="taxNumber"
              type="text"
              value={formData.taxNumber}
              onChange={handleChange}
              placeholder={language === 'pl' ? 'Np. 1234567890' : 'Optional'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.address[language]} <span className="text-red-500">*</span></label>
            <input
              required
              name="address"
              type="text"
              value={formData.address}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.city[language]} <span className="text-red-500">*</span></label>
              <input
                required
                name="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.zip[language]} <span className="text-red-500">*</span></label>
              <input
                required
                name="zip"
                type="text"
                value={formData.zip}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
              />
            </div>
          </div>

          {submitError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-emerald-200 mt-8"
          >
            {isSubmitting ? 'Processing...' : t.placeOrder[language]}
          </button>
        </form>
      </div>

      <div className="bg-gray-50 p-8 rounded-2xl h-fit">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t.cart[language]} Summary</h3>
        <div className="space-y-4">
          {cart.map((item) => {
            const minQty = item.minQuantity || 6;
            return (
            <div key={item.id} className="flex flex-col gap-3 pb-4 border-b border-gray-200 last:border-0">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-12 h-12 bg-white rounded-md overflow-hidden border border-gray-200 flex-shrink-0">
                    <img src={item.image} alt="" className="w-full h-full object-cover"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{item.name}</p>
                    <p className="text-gray-500 text-xs">{item.price.toFixed(2)} zł</p>
                  </div>
                </div>
                <p className="font-medium text-gray-900 ml-2">{(item.price * item.quantity).toFixed(2)} zł</p>
              </div>
              
              {/* Quantity Controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 bg-white rounded-lg px-2 py-1 border border-gray-200">
                  <button 
                    onClick={() => {
                      if (item.quantity <= minQty) {
                        removeFromCart(item.id);
                      } else {
                        updateQuantity(item.id, item.quantity - minQty);
                      }
                    }}
                    className={`p-1 rounded-md text-gray-500 ${item.quantity <= minQty ? 'hover:bg-red-50 hover:text-red-500' : 'hover:bg-gray-100'}`}
                    title={item.quantity <= minQty ? t.remove[language] : "Decrease quantity"}
                  >
                    {item.quantity <= minQty ? <Trash2 className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                  </button>
                  <span className="w-8 text-center font-medium text-sm">{item.quantity}</span>
                  <button 
                    onClick={() => updateQuantity(item.id, item.quantity + minQty)}
                    className="p-1 rounded-md hover:bg-gray-100 text-gray-500"
                    title="Increase quantity"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                {item.quantity === minQty && (
                  <span className="text-xs text-orange-500">Min: {minQty}</span>
                )}
              </div>
            </div>
          );
          })}
          
          <div className="border-t border-gray-200 pt-4 mt-4 space-y-2">
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>{t.subtotal[language]}</span>
              <span>{subtotal.toFixed(2)} zł</span>
            </div>
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>{t.vat[language]}</span>
              <span>{vat.toFixed(2)} zł</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-gray-200">
              <span className="font-bold text-gray-900 text-lg">{t.totalGross[language]}</span>
              <span className="font-bold text-emerald-600 text-2xl">{total.toFixed(2)} zł</span>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};